import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { Eye, EyeOff } from "lucide-react";
import { commonSchemas, validationUtils } from "../../utils";
import { Button, Card, FormField, Input, useToast } from "../ui";

interface LoginFormProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  redirectTo?: string;
  showRememberMe?: boolean;
  showForgotPassword?: boolean;
  showSignUp?: boolean;
  className?: string;
}

enum VIEW {
  TEXT = "text",
  PASSWORD = "password",
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  onError,
  redirectTo = "/",
  showRememberMe = true,
  showForgotPassword = true,
  showSignUp = true,
  className,
}) => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [view, setView] = useState<VIEW>(VIEW.PASSWORD);
  const { login, isLoading } = useAuth();
  const { success, error } = useToast();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string>("");

  // Use the same simple rules as SetPassword (only required / min length)
  const validator = validationUtils.createValidator(commonSchemas.userLogin);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    if (generalError) setGeneralError("");
  };

  const validateForm = (): boolean => {
    const validationResult = validator.validate(formData);
    if (!validationResult.isValid) {
      const newErrors: Record<string, string> = {};
      if (validationResult.fieldErrors) {
        Object.entries(validationResult.fieldErrors).forEach(
          ([field, fieldErrors]) => {
            if (fieldErrors?.[0]) newErrors[field] = fieldErrors[0].message;
          },
        );
      }
      setErrors(newErrors);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setGeneralError("");
      const res = await login(formData.email, formData.password);
      success(
        res?.message ||
          t("signInSuccess", { defaultValue: "Signed in successfully" }),
      );

      // Respect intended URL if present, else fallback
      // const from = (location.state as any)?.from?.pathname || redirectTo || '/dashboard';
      // navigate(from, { replace: true });

      window.location.href = res?.redirectUrl || redirectTo || "/";
      // onSuccess?.();
    } catch (err: any) {
      console.error("Login error:", err);
      const msg =
        err?.message || t("loginFailed", { defaultValue: "Sign in failed" });
      setGeneralError(msg);
      error(msg);
      onError?.(err as Error);
    }
  };

  const handleForgotPassword = () => navigate("/forgot-password");

  return (
    <Card
      className={`bg-transparent dark:bg-transparent rounded-none border-0 shadow-none p-0 max-w-xl w-full mx-auto ${className || ""}`}
    >
      <div className="p-0">
        {/* Header */}
        {/* <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('signIn')}</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('signInDescription')}</p>
        </div> */}

        {/* General Error */}
        {/* {generalError && <Alert variant="error" className="mb-4">{generalError}</Alert>} */}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Email */}
          <FormField
            label={t("email", { defaultValue: "Email address" })}
            required
            error={errors.email}
          >
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              className="py-5 bg-transparent dark:bg-transparent"
              placeholder={t("emailPlaceholder", {
                defaultValue: "Enter your email address",
              })}
              disabled={isLoading}
              autoComplete="email"
              autoFocus
            />
          </FormField>

          {/* Password — SIMPLE INPUT */}
          <FormField
            label={t("password", { defaultValue: "Password" })}
            required
            error={errors.password}
          >
            <div className="relative">
              <Input
                type={view}
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                className="py-5 bg-transparent dark:bg-transparent"
                placeholder={t("passwordPlaceholder", {
                  defaultValue: "Enter your password",
                })}
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="absolute top-3 right-3 cursor-pointer text-gray-500"
                onClick={() =>
                  setView(view === VIEW.PASSWORD ? VIEW.TEXT : VIEW.PASSWORD)
                }
                aria-label={
                  view === VIEW.PASSWORD ? "Show password" : "Hide password"
                }
              >
                {view === VIEW.PASSWORD ? (
                  <EyeOff size={20} />
                ) : (
                  <Eye size={20} />
                )}
              </button>
            </div>
          </FormField>

          {/* Submit */}
          <Button
            className="w-auto bg-primary-button hover:bg-primary-button font-bold text-md"
            type="submit"
            size="md"
            loading={isLoading}
            fullWidth
            disabled={isLoading}
          >
            {isLoading
              ? t("signingIn", { defaultValue: "Signing in..." })
              : t("signIn", { defaultValue: "Sign in" })}
          </Button>

          {/* Remember / Forgot */}
          <div className="flex items-center justify-between">
            {showForgotPassword && (
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={isLoading}
                className="text-sm font-semibold underline text-primary-button hover:text-primary-button dark:text-primary-button dark:hover:text-primary-button disabled:opacity-50"
              >
                {t("forgotPassword", { defaultValue: "Forgot password?" })}
              </button>
            )}
          </div>
        </form>
      </div>
    </Card>
  );
};
